/* -------------------------- Design imports start -------------------------- */
import { Grid } from '@mui/material'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import CompareVersionDrawer from './CompareVersionDrawer'
import VersionTimeline from './VersionTimeline'
import MultilevelDrawer from '../../../components/layout/MultilevelDrawer'
/* --------------------------- Design imports end --------------------------- */
/* ------------------------ Functional imports start ------------------------ */
import LogTool from '../../../logger/logTools'
import { fetchVersionHistory } from '../api/versionApi'
import { type Version, type VersionComparison } from '../utils/types'


/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  formState: {
    state: any | undefined
    setState: React.Dispatch<React.SetStateAction<any | undefined>>
  }
  onConfirm: () => void
  onRevertVersion: (version: Version) => void
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function VersionDrawer(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { open, setOpen, formState, onConfirm } = props
  const log = new LogTool({ context: 'VersionDrawer', enable: false, logLevel: 'warn' })
  const { t } = useTranslation(['common'])
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const [versions, setVersions] = React.useState<Version[]>([])
  const [versionDifferences, setVersionDifferences] = React.useState<VersionComparison | undefined>(
    undefined
  )
  const [compareOpen, setCompareOpen] = React.useState<boolean>(false)
  const [, setRevertVersion] = React.useState<Version | undefined>(undefined)
  /* ----------------------------- Data states end ---------------------------- */

  log.debug('formStateVersion', formState)
  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (open && formState.state?.self) {
      void loadVersions(formState.state.self)
    }
  }, [formState.state?.self, open])

  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  async function loadVersions(objectSelf: string) {
    log.debug('fetchVersions', objectSelf)
    try {
      const response = await fetchVersionHistory(objectSelf)
      setVersions(response)
    } catch (error) {
      log.error(error)
    }
  }

  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = () => {
    setOpen(false)
  }

  const handleRevertVersion = (version: Version) => {
    setRevertVersion(version)
    props.onRevertVersion(version)
  }
  /* ------------------------- Callback functions end ------------------------- */
  /* ------------------------- Render constants start ------------------------- */

  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */

  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        size="small"
        onClose={handleClose}
        title={t('common:content.label.versionHistory') }
        confirmButtonProps={{
          // hide the confirm button
          hidden: true,
          text: '',
          disabled: true,
          // do nothing on confirm
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onConfirm: () => {},
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <VersionTimeline
              versions={versions}
              formState={formState}
              setOpen={setOpen}
              setVersionDifferences={setVersionDifferences}
              setCompareOpen={setCompareOpen}
              onRevertVersion={handleRevertVersion}
            />
          </Grid>
        </Grid>
      </MultilevelDrawer>
      <CompareVersionDrawer
        open={compareOpen}
        setOpen={setCompareOpen}
        formState={formState}
        versionDifferences={versionDifferences}
        onConfirm={onConfirm}
      />
    </>
  )
}
